// src/mobile_pages/meeting/MobileScheduleCalendar.jsx
// 모바일 전용 일정 관리 페이지 (PC ScheduleCalendar.jsx와 분리)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Plus, Calendar as CalendarIcon, X } from "lucide-react";
import Calendar, {
    buildStatusCounts,
    getKRHolidayMap,
} from "../../components/common/feature/Calendar";
import SidePanel from "../../components/common/layout/SidePanel";
import { scheduleApi } from "../../api/schedules";
import { fetchMembership } from "../../api/users/membership";
import MobileScheduleDetail from "./MobileScheduleDetail";
import MobileScheduleNew from "./MobileScheduleNew";

export default function MobileScheduleCalendar() {
    const { id: projectId } = useParams();
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const dateStr = format(selectedDate, "yyyy-MM-dd");

    const [selectedScopes, setSelectedScopes] = useState([
        "project",
        "company",
        "personal",
    ]);

    const [loading, setLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    const [panelOpen, setPanelOpen] = useState(false);
    const [panelMode, setPanelMode] = useState(null);
    const [activeItem, setActiveItem] = useState(null);

    // 하단 시트 (선택한 날짜의 일정 목록)
    const [sheetOpen, setSheetOpen] = useState(false);

    // --- 소속 회사 로드 ---
    useEffect(() => {
        (async () => {
            try {
                const res = await fetchMembership();
                const arr = res.data?.memberships ?? [];
                const primary = arr.find((m) => m.is_primary) ?? arr[0] ?? null;
                setCompanyId(primary?.company ?? null);
            } catch (e) {
                console.error("company load error", e);
            }
        })();
    }, []);

    const toggleScope = (type) => {
        setSelectedScopes((prev) =>
            prev.includes(type) ? prev.filter((v) => v !== type) : [...prev, type]
        );
    };

    // --- 일정 로드 ---
    const fetchSchedules = useCallback(async () => {
        setLoading(true);
        try {
            let list = [];

            if (selectedScopes.includes("project") && projectId) {
                const res = await scheduleApi.listProject(projectId);
                list = [...list, ...(res.data?.results ?? res.data ?? [])];
            }

            if (selectedScopes.includes("company") && companyId) {
                const res = await scheduleApi.listCompany(companyId);
                list = [...list, ...(res.data?.results ?? res.data ?? [])];
            }

            if (selectedScopes.includes("personal")) {
                const res = await scheduleApi.listPersonal();
                list = [...list, ...(res.data?.results ?? res.data ?? [])];
            }

            setEvents(list);
        } catch (err) {
            console.error("fetchSchedules failed:", err);
            setEvents([]);
        } finally {
            setLoading(false);
        }
    }, [selectedScopes, projectId, companyId]);

    useEffect(() => {
        fetchSchedules();
    }, [selectedScopes, fetchSchedules]);

    // 리스트 필터링 (선택한 날짜의 일정)
    const list = useMemo(
        () => events.filter((e) => (e.start ?? "").slice(0, 10) === dateStr),
        [events, dateStr]
    );

    // 상태 dot 구성
    const statusCountsByDate = useMemo(
        () =>
            buildStatusCounts(
                events,
                (m) => (m.start ?? "").slice(0, 10),
                (m) =>
                    m.scope === "personal"
                        ? "green"
                        : m.scope === "company"
                            ? "red"
                            : "blue"
            ),
        [events]
    );

    // 공휴일 Map 관리 (Calendar 내부 위임 가능하나, 여기서도 state 관리하여 넘김)
    const [activeYear, setActiveYear] = useState(selectedDate.getFullYear());
    const [holidayMap, setHolidayMap] = useState({});

    useEffect(() => {
        (async () => {
            const map = await getKRHolidayMap(activeYear);
            setHolidayMap(prev => ({ ...prev, ...map }));
        })();
    }, [activeYear]);

    // 날짜 선택 시 하단 시트 열기
    const handleDateChange = (nextDate) => {
        setSelectedDate(nextDate);
        setSheetOpen(true);
    };

    // Scope label/color helper
    const getScopeStyle = (scope) => {
        if (scope === "personal") return { bg: "bg-green-50", text: "text-green-700", border: "border-green-200", label: "개인" };
        if (scope === "company") return { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "회사" };
        return { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", label: "프로젝트" };
    };

    // 사이드 패널 너비 계산
    const panelWidth = useMemo(() => {
        if (typeof window === "undefined") return 420;
        return Math.min(Math.max(window.innerWidth, 360), 520); // 모바일은 꽉 차게 변경
    }, []);

    return (
        <div className="relative min-h-screen bg-gradient-to-b from-slate-100 to-slate-50 pb-28">
            {/* Header */}
            <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-lg px-4 py-4 border-b border-slate-100/50 safe-area-top">
                <div className="flex items-center justify-between max-w-md mx-auto">
                    <div>
                        <p className="text-[11px] text-blue-600 font-bold tracking-wide uppercase mb-1">회의/협업</p>
                        <h1 className="text-[22px] font-bold text-slate-900 tracking-tight">일정 관리</h1>
                    </div>
                    <button
                        onClick={() => {
                            setPanelMode("create");
                            setPanelOpen(true);
                        }}
                        className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30 active:scale-95 transition-all"
                    >
                        <Plus size={22} className="text-white" />
                    </button>
                </div>
            </header>

            <main className="px-4 pt-5 max-w-md mx-auto">
                {/* Scope Filters */}
                <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1 -mx-1 px-1">
                    {[
                        { key: "project", label: "프로젝트", activeBg: "bg-blue-500", activeText: "text-white", dot: "bg-blue-500" },
                        { key: "company", label: "회사", activeBg: "bg-red-500", activeText: "text-white", dot: "bg-red-500" },
                        { key: "personal", label: "개인", activeBg: "bg-green-500", activeText: "text-white", dot: "bg-green-500" },
                    ].map((opt) => {
                        const active = selectedScopes.includes(opt.key);
                        return (
                            <button
                                key={opt.key}
                                onClick={() => toggleScope(opt.key)}
                                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold transition-all whitespace-nowrap shadow-sm ${active
                                    ? `${opt.activeBg} ${opt.activeText} shadow-md`
                                    : "bg-white text-slate-500 border border-slate-200 hover:border-slate-300"
                                    }`}
                            >
                                <span className={`w-2 h-2 rounded-full ${active ? 'bg-white/80' : opt.dot}`} />
                                {opt.label}
                            </button>
                        );
                    })}
                </div>

                {/* Calendar Card */}
                <section className="rounded-[28px] border border-slate-100/80 bg-white px-5 pt-5 pb-6 shadow-[0_8px_40px_rgba(15,23,42,0.12)]">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <div className="w-8 h-8 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
                            <p className="text-sm text-slate-400 mt-3">일정을 불러오는 중...</p>
                        </div>
                    ) : (
                        <>
                            <Calendar
                                value={selectedDate}
                                onChange={handleDateChange}
                                statusCountsByDate={statusCountsByDate}
                                holidayMap={holidayMap}
                                onMonthChange={setActiveYear} // 연도 변경 감지
                                showCounts={true}
                            />
                            {/* Legend */}
                            <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-slate-100">
                                <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                                    <span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> 프로젝트
                                </div>
                                <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                                    <span className="w-2.5 h-2.5 rounded-full bg-red-500" /> 회사
                                </div>
                                <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                                    <span className="w-2.5 h-2.5 rounded-full bg-green-500" /> 개인
                                </div>
                            </div>
                        </>
                    )}
                </section>
            </main>

            {/* Bottom Sheet - 선택한 날짜의 일정 목록 */}
            <div
                className={[
                    "pointer-events-none fixed inset-0 z-[900] flex items-end justify-center transition-opacity duration-300",
                    sheetOpen ? "opacity-100" : "opacity-0",
                ].join(" ")}
            >
                {/* Overlay */}
                <div
                    className="absolute inset-0 bg-slate-900/25 backdrop-blur-[2px]"
                    style={{ pointerEvents: sheetOpen ? "auto" : "none" }}
                    onClick={() => setSheetOpen(false)}
                />

                {/* Sheet */}
                <div
                    className={[
                        "pointer-events-auto w-full max-w-md transform-gpu transition-transform duration-300 ease-out",
                        sheetOpen ? "translate-y-0" : "translate-y-full",
                    ].join(" ")}
                >
                    <div className="mx-4 mb-0 overflow-hidden rounded-t-3xl border border-slate-100 bg-white shadow-xl">
                        {/* Grip */}
                        <div className="flex items-center justify-center py-3">
                            <div className="h-1.5 w-10 rounded-full bg-slate-200" />
                        </div>

                        {/* Date + Create Button */}
                        <div className="flex items-center justify-between gap-3 px-5 pb-2">
                            <div className="min-w-0">
                                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
                                    선택한 날짜
                                </p>
                                <p className="mt-1 text-[15px] font-semibold text-slate-900">
                                    {format(selectedDate, "yyyy년 MM월 dd일")}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setSheetOpen(false);
                                    setPanelMode("create");
                                    setPanelOpen(true);
                                }}
                                className="shrink-0 rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-sm active:scale-95"
                            >
                                일정 등록
                            </button>
                        </div>

                        {/* Schedule List */}
                        <div className="px-5 pb-5 pt-2">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">
                                일정 목록
                            </p>
                            {list.length === 0 ? (
                                <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500 text-center">
                                    선택한 날짜에 일정이 없습니다.
                                </p>
                            ) : (
                                <div
                                    className="space-y-2 overflow-y-auto"
                                    style={{ maxHeight: "46vh" }}
                                >
                                    {list.map((row) => {
                                        const timeLabel = row.start ? format(new Date(row.start), "HH:mm") : "--:--";
                                        const scopeStyle = getScopeStyle(row.scope);

                                        return (
                                            <button
                                                key={row.id}
                                                type="button"
                                                onClick={() => {
                                                    setActiveItem(row);
                                                    setPanelMode("view");
                                                    setPanelOpen(true);
                                                    setSheetOpen(false);
                                                }}
                                                className="group flex w-full items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3 text-left shadow-sm active:scale-[0.98] transition-transform"
                                            >
                                                {/* Time Badge */}
                                                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600/10 text-[11px] font-semibold text-blue-700">
                                                    {timeLabel}
                                                </div>

                                                {/* Content */}
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-[14px] font-semibold text-slate-900 group-hover:text-blue-700">
                                                        {row.title}
                                                    </p>
                                                    <div className="mt-1 flex flex-wrap gap-1">
                                                        <span
                                                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] border ${scopeStyle.bg} ${scopeStyle.text} ${scopeStyle.border}`}
                                                        >
                                                            {scopeStyle.label}
                                                        </span>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Side Panel - title={null}로 헤더 숨김 (이중 닫기 버튼 방지) */}
            <SidePanel
                isOpen={panelOpen}
                lockScroll={false}
                onClose={() => setPanelOpen(false)}
                width={panelWidth}
                title={null} // 헤더 중복 제거
            >
                {panelMode === "create" && (
                    <MobileScheduleNew
                        projectId={projectId}
                        companyId={companyId}
                        scheduleType="project"
                        onSaved={() => {
                            fetchSchedules();
                            setPanelOpen(false);
                        }}
                        onClose={() => setPanelOpen(false)}
                    />
                )}

                {panelMode === "view" && activeItem && (
                    <MobileScheduleDetail
                        item={activeItem}
                        onEdit={() => setPanelMode("edit")}
                        onDeleted={() => {
                            fetchSchedules();
                            setPanelOpen(false);
                        }}
                        onClose={() => setPanelOpen(false)}
                    />
                )}

                {panelMode === "edit" && activeItem && (
                    <MobileScheduleNew
                        initial={activeItem}
                        mode="edit"
                        projectId={projectId}
                        companyId={companyId}
                        scheduleType={activeItem.scope}
                        onSaved={() => {
                            fetchSchedules();
                            setPanelOpen(false);
                        }}
                        onClose={() => setPanelOpen(false)}
                    />
                )}
            </SidePanel>
        </div>
    );
}
